import { useState, useEffect } from "react";
import { breakTypeApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { 
  Coffee, Utensils, Clock, Plus, Pencil, Trash2, AlertCircle, Loader2
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/AuthContext";
import { FeatureGate } from "../components/FeatureGate";

// Available icons for breaks
const BREAK_ICONS = [
  { value: "coffee", label: "Coffee", icon: Coffee },
  { value: "utensils", label: "Food/Lunch", icon: Utensils },
  { value: "clock", label: "Clock", icon: Clock },
];

const getBreakIcon = (iconName) => {
  const found = BREAK_ICONS.find(i => i.value === iconName);
  return found ? found.icon : Coffee;
};

const Breaks = () => {
  const { subscriptionTier } = useAuth();
  
  // Break types state
  const [breakTypes, setBreakTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [breakDialogOpen, setBreakDialogOpen] = useState(false);
  const [editingBreak, setEditingBreak] = useState(null);
  const [savingBreak, setSavingBreak] = useState(false);
  const [breakForm, setBreakForm] = useState({
    name: "",
    icon: "coffee",
    duration_minutes: 30,
    is_paid: false,
    is_required: false,
    min_shift_hours: 6,
  });

  // Fetch break types
  const fetchBreakTypes = async () => {
    try {
      const response = await breakTypeApi.getAll();
      setBreakTypes(response.data);
    } catch (error) {
      console.error("Error fetching break types:", error);
      toast.error("Failed to load break types");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBreakTypes();
  }, []);

  // Open break dialog
  const openBreakDialog = (breakType = null) => {
    if (breakType) {
      setEditingBreak(breakType);
      setBreakForm({
        name: breakType.name,
        icon: breakType.icon || "coffee",
        duration_minutes: breakType.duration_minutes,
        is_paid: breakType.is_paid,
        is_required: breakType.is_required || false,
        min_shift_hours: breakType.min_shift_hours || 6,
      });
    } else {
      setEditingBreak(null);
      setBreakForm({
        name: "",
        icon: "coffee",
        duration_minutes: 30,
        is_paid: false,
        is_required: false,
        min_shift_hours: 6,
      });
    }
    setBreakDialogOpen(true);
  };

  // Save break type
  const handleSaveBreak = async () => {
    if (!breakForm.name.trim()) {
      toast.error("Please enter a break name");
      return;
    }

    setSavingBreak(true);
    try {
      if (editingBreak) {
        await breakTypeApi.update(editingBreak.id, breakForm);
        toast.success("Break type updated");
      } else {
        await breakTypeApi.create(breakForm);
        toast.success("Break type created");
      }
      setBreakDialogOpen(false);
      fetchBreakTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save break type");
    } finally {
      setSavingBreak(false);
    }
  };

  // Delete break type
  const handleDeleteBreak = async (breakType) => {
    if (!confirm(`Are you sure you want to delete "${breakType.name}"?`)) return;
    
    try {
      await breakTypeApi.delete(breakType.id);
      toast.success("Break type deleted");
      fetchBreakTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete break type");
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Break Types</h1>
          <p className="text-muted-foreground">Configure break types that can be added to shifts</p>
        </div>
        <Button onClick={() => openBreakDialog()} data-testid="add-break-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Break Type
        </Button>
      </div>

      <FeatureGate feature="breakManagement" tier={subscriptionTier}>
        <Card>
          <CardContent className="pt-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : breakTypes.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Coffee className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <h3 className="font-medium mb-2">No break types configured yet</h3>
                <p className="text-sm mb-4">Add break types like lunch or coffee breaks</p>
                <Button onClick={() => openBreakDialog()} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Break Type
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {breakTypes.map((bt) => {
                  const IconComponent = getBreakIcon(bt.icon);
                  return (
                    <div
                      key={bt.id}
                      className="flex items-center gap-4 p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                      data-testid={`break-type-${bt.id}`}
                    >
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <IconComponent className="w-6 h-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-lg">{bt.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {bt.duration_minutes} minutes • {bt.is_paid ? 'Paid' : 'Unpaid'}
                          {bt.is_required && bt.min_shift_hours > 0 && (
                            <span className="ml-2">
                              • Required for shifts over {bt.min_shift_hours}h
                            </span>
                          )}
                          {!bt.is_required && (
                            <span className="ml-2">• Optional</span>
                          )}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {bt.is_required && (
                          <div className="flex items-center gap-1 text-amber-600 dark:text-amber-500">
                            <AlertCircle className="w-4 h-4" />
                            <span className="text-xs font-medium">Required</span>
                          </div>
                        )}
                        {!bt.is_paid && (
                          <span className="text-xs text-muted-foreground">Deducted from pay</span>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openBreakDialog(bt)}
                          data-testid={`edit-break-${bt.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteBreak(bt)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-break-${bt.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </FeatureGate>

      {/* Break Type Dialog */}
      <Dialog open={breakDialogOpen} onOpenChange={setBreakDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingBreak ? "Edit Break Type" : "Add Break Type"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="break-name">Name</Label>
              <Input
                id="break-name"
                value={breakForm.name}
                onChange={(e) => setBreakForm({ ...breakForm, name: e.target.value })}
                placeholder="e.g., Lunch Break"
                data-testid="break-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="break-icon">Icon</Label>
              <Select
                value={breakForm.icon}
                onValueChange={(v) => setBreakForm({ ...breakForm, icon: v })}
              >
                <SelectTrigger data-testid="break-icon-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BREAK_ICONS.map((icon) => (
                    <SelectItem key={icon.value} value={icon.value}>
                      <div className="flex items-center gap-2">
                        <icon.icon className="w-4 h-4" />
                        {icon.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="break-duration">Duration (minutes)</Label>
              <Input
                id="break-duration"
                type="number"
                min={5}
                max={120}
                value={breakForm.duration_minutes}
                onChange={(e) => setBreakForm({ ...breakForm, duration_minutes: parseInt(e.target.value) || 30 })}
                data-testid="break-duration-input"
              />
            </div>
            
            <div className="flex items-center justify-between p-3 rounded-lg border">
              <div>
                <Label htmlFor="break-paid" className="font-medium">Paid Break</Label>
                <p className="text-xs text-muted-foreground">Break time counts towards pay</p>
              </div>
              <Switch
                id="break-paid"
                checked={breakForm.is_paid}
                onCheckedChange={(c) => setBreakForm({ ...breakForm, is_paid: c })}
                data-testid="break-paid-toggle"
              />
            </div>
            
            <div className="space-y-3 p-3 rounded-lg border">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="break-required" className="font-medium">Required Break</Label>
                  <p className="text-xs text-muted-foreground">Automatically prompt for this break</p>
                </div>
                <Switch
                  id="break-required"
                  checked={breakForm.is_required}
                  onCheckedChange={(c) => setBreakForm({ ...breakForm, is_required: c })}
                  data-testid="break-required-toggle"
                />
              </div>
              
              {breakForm.is_required && (
                <div className="space-y-2 pt-2 border-t">
                  <Label htmlFor="min-shift-hours">Minimum Shift Duration (hours)</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Break will be required for shifts longer than this
                  </p>
                  <Input
                    id="min-shift-hours"
                    type="number"
                    min={1}
                    max={12}
                    step={0.5}
                    value={breakForm.min_shift_hours}
                    onChange={(e) => setBreakForm({ ...breakForm, min_shift_hours: parseFloat(e.target.value) || 6 })}
                    data-testid="min-shift-hours-input"
                  />
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBreakDialogOpen(false)} disabled={savingBreak}>
              Cancel
            </Button>
            <Button onClick={handleSaveBreak} disabled={savingBreak || !breakForm.name.trim()} data-testid="save-break-btn">
              {savingBreak ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
              ) : (
                editingBreak ? "Update" : "Create"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Breaks;
